import { AnchoredPanelComponent, DetailView, DetailViewsPanel, GroupItem, useNavigation } from "o4a-react";
import * as React from "react";
import { useParams, useSearchParams } from "react-router-dom";
import * as Messages from "../../codegen/Messages";
import { ConsoleContext } from "../../console/ConsoleContext";
import { DetailsPanelId, PageId, PageRegistrationConfig, SETTINGS_ROUTE } from "../../constants/pluginConstants";
import { SettingAppearance } from "./SettingAppearance";
import { SettingLanguage } from "./SettingLanguage";
import { SettingSignout } from "./SettingSignout";
import { SettingSubscriptionsLocations } from "./SettingSubscriptionsLocations";

const registrationIds = PageRegistrationConfig[PageId.SETTINGS].map(config => config.key);

const getViewId = (panelId: string | undefined): string => {
  switch (panelId) {
    case DetailsPanelId.SIGNOUT:
      return registrationIds[3];
    case DetailsPanelId.LANGUAGE:
      return registrationIds[2];
    case DetailsPanelId.APPEARANCE:
      return registrationIds[1];
    case DetailsPanelId.SUBSCRIPTIONS:
    default:
      return registrationIds[0];
  }
};

export interface SettingsPageProps {
  componentRef?: (ref: AnchoredPanelComponent) => void;
  onOpen?: () => void;
  onClose?: () => void;
}

export const SettingsPage = ({ componentRef, onOpen, onClose }: SettingsPageProps): JSX.Element => {
  const { panelId } = useParams();
  const [searchParams] = useSearchParams();

  const { back, navigateToPanel, navigateToSelf } = useNavigation(ConsoleContext);

  React.useEffect(() => {
    // In case page was navigated to directly by entring its URL in the browser
    const registrationConfig = PageRegistrationConfig[PageId.SETTINGS].find(
      config => config.panelPath === panelId,
    );
    const urlQueryParams = searchParams.toString() ? `?${searchParams.toString()}` : "";
    const path = `${SETTINGS_ROUTE}/${panelId}${urlQueryParams}`;
    navigateToSelf(path, registrationConfig?.key || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    onOpen?.();

    return () => onClose?.();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onMenuItemSelect = (id: string): void => navigateToPanel(id);

  const detailViews: DetailView[] = [
    {
      id: registrationIds[0],
      content: (
        <SettingSubscriptionsLocations />
      ),
    },
    {
      id: registrationIds[1],
      content: (
        <SettingAppearance />
      ),
    },
    {
      id: registrationIds[2],
      content: (
        <SettingLanguage />
      ),
    },
    {
      id: registrationIds[3],
      content: (
        <SettingSignout />
      ),
    },
  ];

  const groupItems: GroupItem[] = [
    {
      items: [
        {
          id: detailViews[0].id,
          name: Messages.labels.subscriptionLocation(),
        },
        {
          id: detailViews[1].id,
          name: Messages.labels.appearance(),
        },
        {
          id: detailViews[2].id,
          name: Messages.labels.language(),
        },
        {
          id: detailViews[3].id,
          name: Messages.labels.signingOutSetting(),
        },
      ],
    },
  ];

  return (
    <DetailViewsPanel
      anchorId="main"
      componentRef={componentRef}
      title={Messages.labels.settings()}
      onClose={back}
      isOpen
      views={detailViews}
      activeViewId={getViewId(panelId)}
      onMenuItemSelect={onMenuItemSelect}
      menu={groupItems}
      menuFixed
    />
  );
};
